import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getRequests, approveRequest, subscribe } from '../../../services/requestStore';

/**
 * EmployeeRequestsPanel
 * --------------------------------------------------------------------------
 * HR Admin–side view of Article 7 worker information requests. Shows:
 *   - Pending requests submitted by employees, with an Approve & Process action
 *   - A small recently-completed list so the admin sees their own work
 *
 * Reads from the shared requestStore so the same item the employee submitted
 * appears here in real time. When the admin clicks Approve, the employee's
 * view of that same request flips to 'completed' immediately.
 *
 * This is the demo's "magic moment", submit on one view, approve on another,
 * see the result back on the first. Investors love this kind of tangible flow.
 */
const EmployeeRequestsPanel = () => {
  const { t, i18n } = useTranslation();
  const [requests, setRequests] = useState(() => getRequests(formatDate));
  const [approvingIds, setApprovingIds] = useState(new Set());

  function formatDate(date) {
    const d = date instanceof Date ? date : new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return i18n.language === 'de' ? `${dd}.${mm}.${yyyy}` : `${dd}/${mm}/${yyyy}`;
  }

  useEffect(() => {
    return subscribe(() => setRequests(getRequests(formatDate)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const pending = requests.filter((r) => r.status === 'pending');
  const recentlyCompleted = requests
    .filter((r) => r.status === 'completed')
    .slice(0, 3);

  const handleApprove = (id) => {
    // Brief processing animation so the moment feels real
    setApprovingIds((prev) => new Set(prev).add(id));
    setTimeout(() => {
      approveRequest(id, formatDate(new Date()));
      setApprovingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 1200);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft mb-6 overflow-hidden">
      {/* Panel Header */}
      <div className="bg-primary/5 border-b border-border px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-primary/10 text-primary-solid rounded-lg flex items-center justify-center shrink-0">
              <Icon name="Inbox" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t('adminRequests.panelTitle')}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('adminRequests.panelSubtitle')}
              </p>
            </div>
          </div>
          {pending.length > 0 && (
            <span className="px-2.5 py-1 bg-warning/15 text-warning rounded-full text-xs font-semibold whitespace-nowrap">
              {t('adminRequests.pendingCount', { count: pending.length })}
            </span>
          )}
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Pending Requests */}
        {pending.length === 0 ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-success/10 text-success rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="CheckCircle" size={20} />
            </div>
            <p className="text-sm font-medium text-foreground">
              {t('adminRequests.noPending')}
            </p>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
              {t('adminRequests.noPendingBody')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((req) => {
              const isApproving = approvingIds.has(req.id);
              return (
                <div
                  key={req.id}
                  id={`pending-request-${req.id}`}
                  className="border border-warning/30 bg-warning/5 rounded-lg p-4 flex items-start gap-4 transition-shadow"
                >
                  <div className="w-10 h-10 bg-warning/15 text-warning rounded-lg flex items-center justify-center shrink-0">
                    <Icon name="Clock" size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground">{req.title}</h3>
                    {req.description && (
                      <p className="text-sm text-muted-foreground mt-0.5 leading-snug">
                        {req.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="User" size={12} />
                        {req.employeeName} <span className="opacity-60">({req.employeeId})</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} />
                        {t('adminRequests.requestedOn', { date: req.requestDate })}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleApprove(req.id)}
                    disabled={isApproving}
                    iconName={isApproving ? 'Loader2' : 'Check'}
                    iconPosition="left"
                  >
                    {isApproving
                      ? t('adminRequests.approving')
                      : t('adminRequests.approveBtn')}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Recently Completed */}
        {recentlyCompleted.length > 0 && (
          <div className="pt-4 border-t border-border">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              {t('adminRequests.recentlyCompleted')}
            </h3>
            <div className="space-y-2">
              {recentlyCompleted.map((req) => (
                <div
                  key={req.id}
                  className="flex items-center gap-3 text-sm py-1.5"
                >
                  <Icon name="CheckCircle" size={14} className="text-success shrink-0" />
                  <span className="text-foreground font-medium truncate flex-1">{req.title}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {req.completedDate
                      ? t('adminRequests.completedOn', { date: req.completedDate })
                      : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeRequestsPanel;
